import { clampZoom, screenToWorld, scrollForScreenAnchor, type ViewState } from './cameraMath';
import type { ScreenPoint, WorldPoint } from '../core/types';

/**
 * Smooth, bounded zoom towards an anchor point. Pure and engine-free.
 *
 * Each step moves the zoom exponentially towards the target in log space
 * (so 0.5→1 feels like 1→2) and recomputes scroll so the anchor WORLD point
 * stays exactly under the anchor SCREEN point. That is what keeps the
 * cursor-to-tile mapping from drifting while a zoom is in progress.
 *
 * Frame-rate independent: the remaining log-distance decays by
 * exp(-smoothing * dt) regardless of how dt is sliced.
 */
export class ZoomAnimator {
  private target: number | null = null;
  private anchorScreen: ScreenPoint = { x: 0, y: 0 };
  private anchorWorld: WorldPoint = { x: 0, y: 0 };

  constructor(
    private readonly smoothing: number,
    private readonly minZoom: number,
    private readonly maxZoom: number,
  ) {}

  get active(): boolean {
    return this.target !== null;
  }

  /** The zoom the camera is heading to (current zoom when idle). */
  targetZoom(current: number): number {
    return this.target ?? current;
  }

  /**
   * Request a zoom by `factor` relative to the current target (so repeated
   * wheel notches accumulate) anchored at a screen point.
   * Returns false when already at the limit in that direction.
   */
  request(view: ViewState, factor: number, anchor: ScreenPoint): boolean {
    const from = this.target ?? view.zoom;
    const next = clampZoom(from * factor, this.minZoom, this.maxZoom);
    if (Math.abs(next - view.zoom) < 1e-9 && this.target === null) return false;
    this.target = next;
    this.anchorScreen = anchor;
    this.anchorWorld = screenToWorld(view, anchor.x, anchor.y);
    return true;
  }

  /** Re-pin the anchor after something else (pan, clamp, resize) moved the camera. */
  reanchor(view: ViewState): void {
    if (this.target === null) return;
    this.anchorWorld = screenToWorld(view, this.anchorScreen.x, this.anchorScreen.y);
  }

  cancel(): void {
    this.target = null;
  }

  /** Advance by dt seconds. Returns the new view, or null when idle. */
  step(view: ViewState, dt: number): ViewState | null {
    if (this.target === null) return null;
    let zoom: number;
    if (this.smoothing <= 0 || !(dt > 0)) {
      zoom = this.smoothing <= 0 ? this.target : view.zoom;
    } else {
      const lt = Math.log(this.target);
      const lz = Math.log(view.zoom);
      zoom = Math.exp(lt + (lz - lt) * Math.exp(-this.smoothing * dt));
    }
    if (Math.abs(zoom - this.target) <= this.target * 1e-3) {
      zoom = this.target;
      this.target = null;
    }
    return { ...view, zoom, ...scrollForScreenAnchor(view, zoom, this.anchorWorld, this.anchorScreen.x, this.anchorScreen.y) };
  }
}
